import { Row } from "antd";
import { useEffect, useMemo, useContext } from "react";
import { CampaignResult, TaskResult } from "models/CommonType";
import { UserResult, User } from "services/types/ProfileTypes";
import { useSearchCampaign, useSearchTask } from "utils/apis";
import { useSearchUser } from 'services/UserService';
import { QueryOption } from "./SearchReducer";
import CampaignList from "./CampaignList";
import TaskList from "./TaskList";
import UserList from "./UserList";
import { AppContext } from "context/AppContext";

interface Props {
  queryOption: QueryOption;
  setDataCount: (count: number | undefined) => void;
  closeHandler?: () => void;
  followHandler?: (user: User) => Promise<void>;
}

const SearchFilterAll: React.FC<Props> = ({
  queryOption,
  setDataCount,
  closeHandler,
  followHandler,
}) => {
  const { authentication } = useContext(AppContext);
  const { authKey } = authentication;

  const { data: campaignResult, isLoading: isCampaignLoading } = useSearchCampaign(queryOption);
  const { data: taskResult, isLoading: isTaskLoading } = useSearchTask(queryOption);

  // Use authKey from AppContext
  const { data: userResult, isLoading: isUserLoading } = useSearchUser({
    keyword: queryOption.keyword || "",
    limit: queryOption.limit,
    page: queryOption.page || 1,
    token: authKey || ""
  });

  const { campaigns, campaignsCount } = useMemo(() => {
    if (!campaignResult) {
      return { campaigns: [], campaignsCount: undefined };
    }
    return {
      campaigns: (campaignResult as CampaignResult)?.results ?? [],
      campaignsCount: (campaignResult as CampaignResult)?.meta?.pagination?.count ?? 0,
    };
  }, [campaignResult]);

  const { tasks, tasksCount } = useMemo(() => {
    if (!taskResult) {
      return { tasks: [], tasksCount: undefined };
    }
    return {
      tasks: (taskResult as TaskResult)?.results ?? [],
      tasksCount: (taskResult as TaskResult)?.meta?.pagination?.count ?? 0,
    };
  }, [taskResult]);

  const { users, usersCount } = useMemo(() => {
    if (!userResult) {
      return { users: [], usersCount: undefined };
    }
    return {
      users: (userResult as UserResult)?.results ?? [],
      usersCount: (userResult as UserResult)?.meta?.pagination?.count ?? 0,
    };
  }, [userResult]);

  useEffect(() => {
    const counts = [campaignsCount, tasksCount, usersCount].filter(count => count != null);
    if (counts.length === 0) {
      setDataCount(undefined);
    } else {
      setDataCount(counts.reduce((sum, count) => sum + (count || 0), 0));
    }
  }, [setDataCount, campaignsCount, tasksCount, usersCount]);

  return (
    <>
      {(isCampaignLoading || isTaskLoading || isUserLoading ||
        !!campaignsCount || !!tasksCount || !!usersCount) && (
          <Row>
            {(isCampaignLoading || !!campaignsCount) && (
              <CampaignList
                campaigns={campaigns}
                isLoading={isCampaignLoading}
                closeHandler={closeHandler}
              />
            )}

            {(isTaskLoading || !!tasksCount) && (
              <TaskList
                tasks={tasks}
                isLoading={isTaskLoading}
                closeHandler={closeHandler}
              />
            )}

            {(isUserLoading || !!usersCount) && (
              <UserList
                users={users}
                isLoading={isUserLoading}
                closeHandler={closeHandler}
                followHandler={followHandler}
              />
            )}
          </Row>
        )}
    </>
  );
};

export default SearchFilterAll;